import { useState } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { Link } from "react-router-dom"
import { Trash2 } from "lucide-react"
import { api, ApiError } from "@/lib/api"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { formatRelative } from "@/lib/utils"

type Keyword = {
  id: string
  keyword: string
  keyword_normalized: string
  is_active: boolean
  created_at: string
  hit_count: number
}

type KeywordHit = {
  paper_id: string
  keyword_id: string
  keyword: string
  matched_field: string
  snippet: string | null
  paper_title_ko: string | null
  paper_title_en: string
  arxiv_id: string | null
  created_at: string
}

export default function KeywordsPage() {
  const qc = useQueryClient()
  const [input, setInput] = useState("")
  const [err, setErr] = useState<string | null>(null)

  const { data: keywords } = useQuery({
    queryKey: ["me-keywords"],
    queryFn: () => api.get<Keyword[]>("/me/keywords"),
  })
  const { data: hits } = useQuery({
    queryKey: ["me-keyword-hits"],
    queryFn: () => api.get<KeywordHit[]>("/me/keyword-hits?limit=30"),
  })

  const add = useMutation({
    mutationFn: (kw: string) => api.post<Keyword>("/me/keywords", { keyword: kw }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["me-keywords"] }); setInput("") },
    onError: (e) => { setErr(e instanceof ApiError ? e.message : "추가 실패") },
  })
  const remove = useMutation({
    mutationFn: (id: string) => api.delete<void>(`/me/keywords/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me-keywords"] }),
  })

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">키워드 트래킹</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          등록한 키워드가 최근 7일의 논문 제목/초록/요약에 등장하면 매시 자동으로 기록됩니다.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>키워드 등록</CardTitle>
          <CardDescription>대소문자 / 공백 무시 매칭 (예: "GraphRAG", "Mamba")</CardDescription>
        </CardHeader>
        <CardContent>
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault()
              setErr(null)
              const v = input.trim()
              if (v) add.mutate(v)
            }}
          >
            <Input
              placeholder="키워드 입력"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              maxLength={80}
            />
            <Button type="submit" disabled={add.isPending || !input.trim()}>
              추가
            </Button>
          </form>
          {err && <p className="mt-2 text-sm text-destructive">{err}</p>}

          {keywords && keywords.length > 0 && (
            <ul className="mt-4 flex flex-wrap gap-2">
              {keywords.filter((k) => k.is_active).map((k) => (
                <li
                  key={k.id}
                  className="flex items-center gap-2 rounded-md bg-secondary px-3 py-1.5 text-sm text-secondary-foreground"
                >
                  <span>{k.keyword}</span>
                  {k.hit_count > 0 && (
                    <span className="rounded bg-primary/15 px-1.5 text-[10px] text-primary">
                      {k.hit_count}
                    </span>
                  )}
                  <button
                    onClick={() => remove.mutate(k.id)}
                    className="opacity-50 transition-opacity hover:opacity-100"
                    title="삭제"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>최근 매치</CardTitle>
          <CardDescription>등록한 키워드가 등장한 논문 (최신순)</CardDescription>
        </CardHeader>
        <CardContent>
          {!hits || hits.length === 0 ? (
            <p className="text-sm text-muted-foreground">매치된 논문이 아직 없습니다.</p>
          ) : (
            <ul className="divide-y divide-border">
              {hits.map((h) => (
                <li key={`${h.keyword_id}-${h.paper_id}`} className="py-3">
                  <div className="mb-1 flex items-center gap-2 text-xs text-muted-foreground">
                    <span className="rounded bg-primary/10 px-1.5 text-primary">{h.keyword}</span>
                    <span>{h.matched_field}</span>
                    <span>·</span>
                    <span>{formatRelative(h.created_at)}</span>
                  </div>
                  <Link
                    to={`/papers/${h.paper_id}`}
                    className="text-base font-medium hover:text-primary"
                  >
                    {h.paper_title_ko || h.paper_title_en}
                  </Link>
                  {h.snippet && (
                    <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{h.snippet}</p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
